/* PreviewDetail.jsx — One navigable preview page per specialization.
   Renders ALL fields exactly as in the JSON, in JSON field order. Nothing hardcoded.
   UI: playful & colorful "After 10th/Inter" style — warm cream bg, pastel section accents.
   Special renders: hero quote, score meters, 7-step workflow timeline, interactive
   quiz, myths two-column, 4-stage roadmap stepper, takeaway callout, related links. */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useSectionNav } from '../hooks/useSectionNav';
import { useRevealOnce } from '../hooks/useRevealOnce';
import {
  ArrowLeft, Bot, Quote, Loader2, AlertTriangle, User, Sparkles, CheckCircle2,
  GraduationCap, Wrench, Briefcase, Building2, BookOpen, Compass, Layers,
  ClipboardCheck, TrendingUp, Scale, Map, Link2, Award, Lightbulb, Target,
  ChevronRight, RotateCcw, MessageSquareQuote, Factory, Users,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LexiTrigger from '../components/lexi/LexiTrigger';
import BuildBadgeCTA from '../components/BuildBadgeCTA';
import { usePreviewQuota } from '../hooks/usePreviewQuota';
import { programFromSlug, programSlug, humanize } from '../lib/previewLibrary';
import PreviewUnlockOverlay from '../components/PreviewUnlockOverlay';
import { LiveOneDayButton } from '../components/stories/OkaRojuPlayer';
import { SubjectUnderstanding } from '../components/subject/SubjectUnderstanding';
import { useAuth } from '../lib/auth';

/* Content package rule: schema keys, pointers, IDs and hashes never render as lesson text. */
const HIDDEN_KEY = /(_pointer(_base)?$|_ids?$|^ids?$|^slug|_edition$|^claim_type$|^content_kind$|^placement$|^match_status$|^source_file$|^destination_type$|^scored$|^type$|^badge_code$|^guidance$|^state_change$|^correction_review$|^changes_learning_or_access_state$|^reference$|^service_reference$|^registry_namespace$|^verification_rule$|^evidence_relationship$|^assessment_kind$|^section_title$|^assessment_kind$|^direction$|^exercise_evidence_kind$|^progress_effect$|^access_rule$|^anchor_subject_id$|^anchor_lesson_id$|^case_id$)/;
const visibleEntries = (obj) => Object.entries(obj || {}).filter(([k, v]) => !HIDDEN_KEY.test(k) && v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && !v.length));
const noteText = (v) => (v && typeof v === 'object' && !Array.isArray(v)) ? Object.values(v).filter((x) => typeof x === 'string').join(' ') : v;

/* ───────── generic building blocks (all content comes from JSON) ───────── */

/* Calm 3-tone palette for Inner Specializations detail pages.
   Hero-primary = teal · Neutral = slate · Sparing highlight = amber (warning
   moments). Non-primary decorative hues (cyan / indigo / purple / blue) now
   all resolve to the teal primary so long pages feel unified instead of
   rainbow-like. Semantic hues (amber = warning, emerald = success, rose =
   risk) are preserved because their meaning shouldn't be flattened. */
const _PRIMARY_TEAL = { card: 'border-teal-200 bg-teal-50/40', icon: 'from-teal-400 to-emerald-500' };
const ACCENTS = {
  teal:    _PRIMARY_TEAL,
  cyan:    _PRIMARY_TEAL,
  indigo:  _PRIMARY_TEAL,
  purple:  _PRIMARY_TEAL,
  blue:    _PRIMARY_TEAL,
  // neutral second tone
  slate:   { card: 'border-slate-200 bg-slate-50/50', icon: 'from-slate-400 to-slate-600' },
  // semantic — kept meaningful
  amber:   { card: 'border-amber-200 bg-amber-50/40', icon: 'from-amber-400 to-orange-500' },  // warning / caution
  emerald: { card: 'border-emerald-200 bg-emerald-50/40', icon: 'from-emerald-400 to-teal-500' }, // success
  rose:    { card: 'border-rose-200 bg-rose-50/40', icon: 'from-rose-400 to-pink-500' },       // risk
};

const Section = ({ id, icon: Icon, title, accent = 'teal', children, testId }) => {
  const pal = ACCENTS[accent] || ACCENTS.teal;
  const revealRef = useRevealOnce();
  return (
    <section ref={revealRef} id={id} className={`reveal bg-white border-2 ${pal.card} rounded-3xl p-5 md:p-7 shadow-sm scroll-mt-52`} data-testid={testId}>
      <div className="flex items-center gap-3 mb-4">
        <span className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${pal.icon} text-white flex items-center justify-center shrink-0 shadow-sm`}>
          <Icon size={18} />
        </span>
        <h2 className="text-lg md:text-xl font-extrabold text-gray-900">{title}</h2>
      </div>
      <div className="text-gray-700 text-base leading-relaxed">{children}</div>
    </section>
  );
};

const BulletList = ({ items, tone = 'teal' }) => (
  <ul className="space-y-2">
    {(items || []).map((x, i) => (
      <li key={i} className="flex gap-2.5">
        <span className={`w-5 h-5 rounded-full ${tone === 'amber' ? 'bg-amber-100 text-amber-700' : 'bg-teal-100 text-teal-700'} flex items-center justify-center mt-0.5 shrink-0`}>
          <ChevronRight size={12} />
        </span>
        <span>{x}</span>
      </li>
    ))}
  </ul>
);

const ChipList = ({ items, tone = 'slate' }) => {
  const tones = {
    slate: 'bg-gray-50 text-gray-700 border-gray-200',
    teal: 'bg-teal-50 text-teal-800 border-teal-200',
    blue: 'bg-indigo-50 text-indigo-800 border-indigo-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
  };
  return (
    <div className="flex flex-wrap gap-2">
      {(items || []).map((x, i) => (
        <span key={i} title={typeof x === 'object' && x ? x.guidance : undefined}
          className={`text-sm px-3 py-1.5 rounded-full border font-medium ${tones[tone]}`}>
          {typeof x === 'object' && x ? (x.name ?? x.title ?? '') : x}
        </span>
      ))}
    </div>
  );
};

/* Small policy / guidance note — resolved text only, never internal keys */
const PolicyNote = ({ text }) => (
  noteText(text) ? <p className="mt-4 text-sm text-gray-500 italic leading-relaxed border-l-2 border-teal-200 pl-3">{noteText(text)}</p> : null
);

/* Generic verbatim value renderer — strings, lists, list-of-dicts, nested dicts */
const Val = ({ v }) => {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'string' && /^https?:\/\//.test(v.trim())) {
    return (
      <a
        href={v.trim()}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-teal-700 font-bold underline underline-offset-2 hover:text-teal-900 break-all"
        data-testid="pv-portal-link"
      >
        {v.trim()}
      </a>
    );
  }
  if (typeof v !== 'object') return <p>{String(v)}</p>;
  if (Array.isArray(v)) {
    if (v.every((x) => typeof x !== 'object' || x === null)) return <BulletList items={v} />;
    if (v.every((x) => x && typeof x === 'object' && 'name' in x && Object.keys(x).length <= 2)) {
      return <ChipList items={v} tone="teal" />;
    }
    return (
      <div className="space-y-3">
        {v.map((x, i) => (
          <div key={i} className="bg-white border-2 border-gray-100 rounded-2xl px-4 py-3.5 shadow-sm">
            <Val v={x} />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {visibleEntries(v).map(([k, val]) => (
        <div key={k}>
          <p className="text-[11px] uppercase tracking-widest font-extrabold text-teal-800 mb-1">{humanize(k)}</p>
          <div className="text-gray-700"><Val v={val} /></div>
        </div>
      ))}
    </div>
  );
};

/* Renders a dict of {key: any} as labelled sub-blocks — keys humanized, values verbatim */
const KVBlocks = ({ obj, chipKeys = [] }) => (
  <div className="space-y-5">
    {visibleEntries(obj).map(([k, v]) => (
      <div key={k}>
        <p className="font-extrabold text-gray-900 mb-2">{humanize(k)}</p>
        {Array.isArray(v) && chipKeys.includes(k)
          ? <ChipList items={v} tone="teal" />
          : <Val v={v} />}
      </div>
    ))}
  </div>
);

/* Snapshot score meter 1..5 */
const Meter = ({ label, value, grad = 'from-teal-400 to-emerald-500' }) => (
  <div data-testid={`pv-meter-${label.replace(/\s+/g, '-').toLowerCase()}`}>
    <div className="flex items-center justify-between mb-1.5">
      <p className="text-sm font-bold text-gray-700">{label}</p>
      <p className="text-sm font-extrabold text-gray-900">{value}/5</p>
    </div>
    <div className="flex gap-1.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={`h-2.5 flex-1 rounded-full transition-colors ${i <= value ? `bg-gradient-to-r ${grad}` : 'bg-gray-200'}`} />
      ))}
    </div>
  </div>
);

/* ───────── special sections ───────── */

const WorkflowTimeline = ({ steps }) => (
  <ol className="relative space-y-6">
    <span className="absolute left-[15px] top-3 bottom-3 w-0.5 bg-gray-200" aria-hidden="true" />
    {(steps || []).map((s) => (
      <li key={s.step} className="relative pl-12" data-testid={`pv-workflow-step-${s.step}`}>
        <span className="absolute left-0 top-0 w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 text-white text-xs font-extrabold flex items-center justify-center shadow-sm">{s.step}</span>
        <p className="font-extrabold text-gray-900 mb-2">{s.title}</p>
        <div className="grid md:grid-cols-3 gap-2.5 text-sm">
          <div className="bg-indigo-50/70 border border-indigo-100 rounded-2xl px-3.5 py-2.5">
            <p className="flex items-center gap-1.5 font-bold text-indigo-900 mb-1"><User size={13} /> Human role</p>
            <p className="text-gray-700">{s.human_role}</p>
          </div>
          <div className="bg-teal-50/70 border border-teal-100 rounded-2xl px-3.5 py-2.5">
            <p className="flex items-center gap-1.5 font-bold text-teal-900 mb-1"><Bot size={13} /> AI Agent role</p>
            <p className="text-gray-700">{s.ai_agent_role}</p>
          </div>
          <div className="bg-amber-50/70 border border-amber-100 rounded-2xl px-3.5 py-2.5">
            <p className="flex items-center gap-1.5 font-bold text-amber-900 mb-1"><Target size={13} /> Student action</p>
            <p className="text-gray-700">{s.student_action}</p>
          </div>
        </div>
      </li>
    ))}
  </ol>
);

/* Interactive yes/no quiz with quiz_result_logic colors */
const SelfCheckQuiz = ({ questions, resultLogic }) => {
  const [answers, setAnswers] = useState({}); // {idx: true|false}
  const answered = Object.keys(answers).length;
  const yesCount = Object.values(answers).filter((a) => a === true).length;
  const done = answered === (questions || []).length && answered > 0;

  let result = null;
  if (done && resultLogic?.scoring_enabled === false) {
    result = { text: resultLogic.interpretation, cls: 'bg-teal-50 border-teal-300 text-teal-900', icon: <Compass className="text-teal-600" size={20} />, testId: 'reflection', extra: resultLogic };
  } else if (done) {
    if (yesCount >= 4) result = { text: resultLogic?.strong_match, cls: 'bg-emerald-50 border-emerald-300 text-emerald-900', icon: <CheckCircle2 className="text-emerald-600" size={20} />, testId: 'strong' };
    else if (yesCount >= 2) result = { text: resultLogic?.moderate_match, cls: 'bg-amber-50 border-amber-300 text-amber-900', icon: <Scale className="text-amber-600" size={20} />, testId: 'moderate' };
    else result = { text: resultLogic?.explore_more, cls: 'bg-gray-100 border-gray-300 text-gray-700', icon: <Compass className="text-gray-500" size={20} />, testId: 'explore' };
  }

  return (
    <div>
      <div className="space-y-3">
        {(questions || []).map((q, i) => {
          const a = answers[i];
          return (
            <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-3 bg-white border-2 border-gray-100 rounded-2xl px-4 py-3.5 shadow-sm" data-testid={`pv-quiz-q-${i}`}>
              <p className="flex-1 text-gray-800">{q.question}</p>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => setAnswers((prev) => ({ ...prev, [i]: true }))}
                  className={`px-5 py-1.5 rounded-full text-sm font-extrabold border-2 transition-all active:scale-95 ${a === true ? 'bg-teal-500 text-white border-teal-500 shadow-sm' : 'bg-white text-gray-600 border-gray-200 hover:border-teal-400'}`}
                  data-testid={`pv-quiz-yes-${i}`}
                >Yes</button>
                <button
                  onClick={() => setAnswers((prev) => ({ ...prev, [i]: false }))}
                  className={`px-5 py-1.5 rounded-full text-sm font-extrabold border-2 transition-all active:scale-95 ${a === false ? 'bg-gray-700 text-white border-gray-700 shadow-sm' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
                  data-testid={`pv-quiz-no-${i}`}
                >No</button>
                {(q.response_options || []).some((o) => /unsure/i.test(o)) && (
                  <button
                    onClick={() => setAnswers((prev) => ({ ...prev, [i]: 'unsure' }))}
                    className={`px-4 py-1.5 rounded-full text-sm font-extrabold border-2 transition-all active:scale-95 ${a === 'unsure' ? 'bg-amber-500 text-white border-amber-500 shadow-sm' : 'bg-white text-gray-600 border-gray-200 hover:border-amber-400'}`}
                    data-testid={`pv-quiz-unsure-${i}`}
                  >Unsure</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex items-center justify-between text-sm text-gray-500 font-semibold">
        <span data-testid="pv-quiz-progress">{answered}/{(questions || []).length} answered · {yesCount} Yes</span>
        {answered > 0 && (
          <button onClick={() => setAnswers({})} className="inline-flex items-center gap-1 hover:text-rose-600" data-testid="pv-quiz-reset">
            <RotateCcw size={12} /> Reset
          </button>
        )}
      </div>
      {result && (
        <div className={`mt-4 border-2 rounded-2xl px-4 py-3.5 cc-pop-in ${result.cls}`} data-testid={`pv-quiz-result-${result.testId}`}>
          <div className="flex items-center gap-3 font-bold">{result.icon} {result.text}</div>
          {result.extra && (
            <div className="mt-3 space-y-3 text-sm font-medium text-gray-800" data-testid="pv-quiz-practical-trial">
              <div><p className="text-[11px] uppercase tracking-widest font-extrabold text-teal-800 mb-1">Practical trial</p><p>{result.extra.practical_trial}</p></div>
              {Array.isArray(result.extra.reflection_after_trial) && <div><p className="text-[11px] uppercase tracking-widest font-extrabold text-teal-800 mb-1">Reflect after the trial</p><BulletList items={result.extra.reflection_after_trial} /></div>}
              {result.extra.decision && <p className="italic text-gray-600">{result.extra.decision}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MythsReality = ({ items }) => (
  <div className="space-y-4">
    {(items || []).map((m, i) => (
      <div key={i} className="grid md:grid-cols-2 gap-3" data-testid={`pv-myth-${i}`}>
        <div className="bg-rose-50/70 border-2 border-rose-200 rounded-2xl px-4 py-3.5">
          <p className="text-[11px] uppercase tracking-widest font-extrabold text-rose-700 mb-1.5">Myth</p>
          <p className="text-gray-800">{m.myth}</p>
        </div>
        <div className="bg-teal-50/70 border-2 border-teal-200 rounded-2xl px-4 py-3.5">
          <p className="text-[11px] uppercase tracking-widest font-extrabold text-teal-700 mb-1.5">Reality</p>
          <p className="text-gray-800">{m.reality}</p>
        </div>
      </div>
    ))}
  </div>
);

const STAGE_GRADS = ['from-teal-400 to-emerald-500', 'from-amber-400 to-orange-500', 'from-rose-400 to-pink-500', 'from-indigo-400 to-violet-500'];
const STAGE_SOFT = ['bg-teal-50/60 border-teal-200', 'bg-amber-50/60 border-amber-200', 'bg-rose-50/60 border-rose-200', 'bg-indigo-50/60 border-indigo-200'];

const RoadmapStepper = ({ stages }) => (
  <div className="grid md:grid-cols-4 gap-4">
    {(stages || []).map((st, i) => (
      <div key={i} className={`relative border-2 rounded-2xl p-4 flex flex-col ${STAGE_SOFT[i % 4]}`} data-testid={`pv-roadmap-stage-${i}`}>
        <span className={`w-8 h-8 rounded-full bg-gradient-to-br ${STAGE_GRADS[i % 4]} text-white text-xs font-extrabold flex items-center justify-center mb-3 shadow-sm`}>{i + 1}</span>
        {i < stages.length - 1 && (
          <span className="hidden md:block absolute top-8 -right-3.5 w-4 border-t-2 border-dashed border-gray-300" aria-hidden="true" />
        )}
        <p className="font-extrabold text-gray-900 text-sm mb-1">{st.stage}</p>
        <p className="text-xs text-gray-600 font-bold mb-2.5">{st.focus}</p>
        <ul className="space-y-1.5 text-sm text-gray-700">
          {(st.student_actions || []).map((a, j) => (
            <li key={j} className="flex gap-1.5"><ChevronRight size={13} className="text-gray-500 mt-0.5 shrink-0" /><span>{a}</span></li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);

/* ───────── sticky in-page nav config (ids match section order) ───────── */
const NAV_CHIPS = [
  { id: 'pv-subjects', label: 'Subjects' },
  { id: 'pv-snapshot', label: 'Snapshot' },
  { id: 'pv-meaning', label: 'Meaning' },
  { id: 'pv-ai-agents', label: 'AI Agents' },
  { id: 'pv-workflow', label: 'Workflow' },
  { id: 'pv-quiz', label: 'Quiz' },
  { id: 'pv-learn', label: 'Learn' },
  { id: 'pv-projects', label: 'Projects' },
  { id: 'pv-scenario', label: 'Scenario' },
  { id: 'pv-careers', label: 'Careers' },
  { id: 'pv-myths', label: 'Myths' },
  { id: 'pv-roadmap', label: 'Roadmap' },
  { id: 'pv-related', label: 'Related' },
  { id: 'pv-takeaway', label: 'Takeaway' },
];

export default function PreviewDetail() {
  const { program: slug, id } = useParams();
  const navigate = useNavigate();
  const [rec, setRecord] = useState(null);
  const [error, setError] = useState(null);
  // Strict commit-and-lock gate: the user MUST be allowed to see this spec.
  // /api/specs/{spec_id}/preview returns 423 if not in their committed branch
  // (pre-commit) or not in their final kept set (post-commit) — and on success
  // returns ONLY this one spec's record (the full dataset never ships).
  const [gateState, setGateState] = useState('checking'); // checking | allowed | denied
  const [gateInfo, setGateInfo] = useState(null);
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const subjectsOpen = params.get('subject') === 'roadmap';
  const openSubjects = () => setParams((p) => { p.set('subject', 'roadmap'); return p; }, { replace: true });
  const closeSubjects = () => {
    setParams((p) => { ['subject', 'su-topic', 'su-reading', 'su-view'].forEach((k) => p.delete(k)); return p; }, { replace: true });
    requestAnimationFrame(() => subjectsBtnRef.current?.focus({ preventScroll: true }));
  };
  const subjectsBtnRef = useRef(null);
  usePreviewQuota(`spec-${slug}-${id}`, 'spec');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const specId = `${slug}_${id}`;
        const res = await axios.get(`${API}/api/specs/${specId}/preview`);
        if (!cancelled) { setRecord(res.data.record); setGateState('allowed'); }
      } catch (e) {
        if (cancelled) return;
        const code = e?.response?.status;
        const d = e?.response?.data?.detail;
        setGateInfo({ code, detail: typeof d === 'object' ? d : { message: d || 'Locked.' } });
        setGateState('denied');
      }
    })();
    return () => { cancelled = true; };
  }, [slug, id]);

  const program = programFromSlug(slug);

  // Active pill + progress rail for the sticky in-page nav.
  const navIds = useMemo(() => NAV_CHIPS.map((c) => c.id), []);
  const { activeId, progress, scrollToId } = useSectionNav(navIds);
  const navStripRef = useRef(null);
  useEffect(() => {
    const strip = navStripRef.current;
    const btn = strip?.querySelector('[aria-current="true"]');
    if (!strip || !btn) return;
    strip.scrollTo({ left: Math.max(0, btn.offsetLeft - (strip.clientWidth - btn.offsetWidth) / 2), behavior: 'smooth' });
  }, [activeId]);

  if (gateState === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFF9F1]">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (gateState === 'denied') {
    return (
      <div className="min-h-screen bg-[#FFF9F1]">
        <Navbar />
        <div className="pt-32 pb-16 px-4">
          <div className="max-w-xl mx-auto text-center bg-white border-2 border-teal-200 rounded-3xl p-10 shadow-md" data-testid="spec-detail-locked">
            <AlertTriangle size={32} className="mx-auto text-rose-500 mb-3" />
            <h1 className="text-2xl font-extrabold text-gray-900">Locked</h1>
            <p className="text-sm text-gray-600 mt-2 leading-relaxed">
              {gateInfo?.detail?.message || 'This preview is not in your set.'}
            </p>
            <Link to="/specializations"
              className="inline-flex items-center justify-center gap-2 mt-6 bg-teal-600 hover:bg-teal-700 text-white font-extrabold px-6 py-3 rounded-full">
              Back to my picks
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (error || (rec && !program)) {
    return (
      <div className="min-h-screen bg-[#FFF9F1]">
        <Navbar />
        <div className="pt-36 px-6 max-w-xl mx-auto text-center">
          <AlertTriangle className="mx-auto text-amber-500 mb-3" size={30} />
          <h1 className="text-xl font-bold text-gray-900 mb-2">{error ? 'Could not load the preview' : 'Preview not found'}</h1>
          <p className="text-gray-600 mb-5">{error || `No preview with id ${id} in ${program || slug}.`}</p>
          <Link to="/previews" className="text-teal-700 font-semibold underline">Browse all previews</Link>
        </div>
      </div>
    );
  }

  if (!rec) {
    return (
      <div className="min-h-screen bg-[#FFF9F1] flex items-center justify-center text-gray-500">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading preview…
      </div>
    );
  }

  const s = rec.snapshot || {};
  const T = (id, fallback) => rec.section_titles?.[id] || fallback;
  const meters = [['Difficulty', s.difficulty, 'from-indigo-400 to-violet-500'], ['Future Demand', s.future_demand, 'from-teal-400 to-emerald-500'], ['AI-Agent Impact', s.ai_agent_impact, 'from-teal-400 to-emerald-500'], ['Automation Risk', s.automation_risk, 'from-amber-400 to-orange-500'], ['Human Judgment Importance', s.human_judgment_importance, 'from-indigo-400 to-violet-500']].filter(([, v]) => typeof v === 'number');
  const acr = rec.ai_career_reality;
  const su = rec.subject_understanding;
  const earning = rec.closing_earning_options;
  const outlook = rec.field_outlook;
  const practice = rec.ai_practice_update;
  const openSubjectTopic = (lessonId) => setParams((p) => { p.set('subject', 'roadmap'); p.set('su-topic', lessonId); p.set('su-reading', '1'); p.set('su-view', 'roadmap'); return p; }, { replace: true });
  const specId = `${slug}_${id}`;

  return (
    <PreviewUnlockOverlay
      pathKey="specializations"
      previewId={slug ? `${slug.toLowerCase()}_${id}` : String(id)}
      itemName={rec.title}
      itemType="specialization"
    >
    <div className="min-h-screen bg-[#FFF9F1]">
      <Navbar />

      {/* ── Hero: title + opening_hook quote (playful gradient) ── */}
      <section className="relative pt-32 md:pt-36 pb-12 px-4 md:px-6 bg-gradient-to-br from-teal-500 via-emerald-500 to-cyan-600 text-white rounded-b-[2.5rem] overflow-hidden">
        <div className="absolute -top-12 right-8 w-48 h-48 rounded-full bg-white/10" aria-hidden="true" />
        <div className="absolute bottom-0 -left-10 w-36 h-36 rounded-full bg-white/10" aria-hidden="true" />
        <div className="max-w-4xl mx-auto relative">
          <Link to="/specializations" className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white mb-5 transition-colors font-semibold" data-testid="pv-back-link">
            <ArrowLeft size={14} /> All previews
          </Link>
          {/* Chips whisper — small, quiet secondary metadata. Duplicates filtered. */}
          <div className="flex flex-wrap items-center gap-1.5 mb-4 text-[10px] font-semibold tracking-wide uppercase">
            {(() => {
              const seen = new Set();
              const chips = [rec.program, rec.branch_or_stream, rec.category]
                .filter(Boolean)
                .filter((c) => {
                  const k = String(c).trim().toLowerCase();
                  if (!k || seen.has(k)) return false;
                  seen.add(k);
                  return true;
                });
              return chips.map((c, i) => (
                <span key={i} className="bg-white/15 text-white/90 px-2.5 py-0.5 rounded-full">{c}</span>
              ));
            })()}
          </div>
          <h1 className="text-3xl md:text-5xl font-bold leading-[1.15] tracking-tight mb-7 cc-pop-in" data-testid="pv-title">{rec.title}</h1>
          {/* Opening hook — the emotional star of the hero */}
          <blockquote className="relative bg-white/10 backdrop-blur border border-white/20 rounded-3xl px-6 py-6 md:px-8 md:py-8" data-testid="pv-opening-hook">
            <Quote size={26} className="text-amber-300 mb-3" />
            <p className="text-lg md:text-2xl text-white leading-[1.5] italic">{rec.opening_hook}</p>
          </blockquote>
          {acr && (
            <div className="mt-4 bg-white/10 backdrop-blur border border-white/20 rounded-3xl px-5 py-5 md:px-7 md:py-6" data-testid="pv-ai-career-reality">
              <div className="flex flex-wrap items-center gap-2 mb-2 text-[11px] font-extrabold uppercase tracking-widest">
                {acr.badge_label && <span className="bg-amber-300 text-amber-950 px-2.5 py-0.5 rounded-full" data-testid="pv-ai-badge-label">{acr.badge_label}</span>}
                {acr.reviewed_on && <span className="text-white/75">Reviewed {acr.reviewed_on}</span>}
              </div>
              {acr.work_bundle_assessed && <p className="text-sm text-white/85 font-semibold mb-2" data-testid="pv-ai-work-bundle">Work assessed: {acr.work_bundle_assessed}</p>}
              <p className="text-[15px] md:text-base text-white leading-relaxed" data-testid="pv-ai-opening-notice">{acr.opening_notice}</p>
              <details className="mt-3 text-sm text-white/90 [&_p]:text-white/90 [&_.text-teal-800]:text-teal-200 [&_.text-gray-700]:text-white/85 [&_.text-gray-900]:text-white [&_.border-gray-100]:border-white/20 [&_.bg-white]:bg-white/10 [&_.bg-teal-100]:bg-white/20 [&_.text-teal-700]:text-white" data-testid="pv-ai-details">
                <summary className="cursor-pointer font-bold text-amber-200 min-h-11 flex items-center">More about this assessment</summary>
                <div className="mt-3"><Val v={Object.fromEntries(visibleEntries(acr).filter(([k]) => !['badge_label', 'reviewed_on', 'work_bundle_assessed', 'opening_notice', 'name', 'program', 'branch_or_stream', 'category'].includes(k)))} /></div>
              </details>
            </div>
          )}
        </div>
      </section>

      {/* ── Sticky in-page nav: active pill + progress rail ── */}
      <div className="lxt-sticky-nav z-30 bg-[#FFF9F1] border-b border-amber-100 shadow-[0_2px_10px_-6px_rgba(0,0,0,0.06)]" data-testid="pv-section-nav">
        <div ref={navStripRef} className="max-w-4xl mx-auto px-4 md:px-6 py-2.5 flex gap-2 overflow-x-auto no-scrollbar">
          {NAV_CHIPS.map((c) => {
            const isActive = c.id === activeId;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => scrollToId(c.id)}
                aria-current={isActive ? 'true' : undefined}
                className={`shrink-0 text-xs font-bold rounded-full px-3.5 py-1.5 border transition-colors ${isActive ? 'border-teal-500 text-teal-900 bg-teal-50' : 'text-gray-600 bg-white border-gray-200 hover:border-teal-400 hover:text-teal-800'}`}
                data-testid={`pv-nav-${c.id}`}
              >{c.label}</button>
            );
          })}
        </div>
        <div className="h-[2px] bg-gray-100/70 relative overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)} data-testid="pv-nav-progress">
          <span
            className="absolute inset-y-0 left-0 will-change-transform"
            style={{ width: '100%', transform: `scaleX(${progress})`, transformOrigin: '0 0', background: '#0D9488', transition: 'transform 90ms linear' }}
          />
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8 space-y-6">

        {/* field_outlook — section 2 (ai-career-outlook), conditional editorial prediction */}
        {outlook && (
          <Section id="pv-outlook" icon={Compass} title={outlook.title || T('ai-career-outlook', 'AI and career outlook')} accent="amber" testId="pv-section-outlook">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="text-[11px] uppercase tracking-widest font-extrabold bg-amber-100 text-amber-900 border border-amber-200 px-2.5 py-1 rounded-full" data-testid="pv-outlook-kind">Editorial scenario · not a job-loss measurement</span>
              {outlook.direction && <span className="text-[11px] uppercase tracking-widest font-extrabold bg-gray-100 text-gray-700 px-2.5 py-1 rounded-full" data-testid="pv-outlook-direction">{humanize(outlook.direction)}</span>}
              {outlook.reviewed_on && <span className="text-sm text-gray-500 font-semibold">Reviewed {outlook.reviewed_on}</span>}
            </div>
            {outlook.work_assessed && <p className="text-sm font-bold text-gray-600 mb-3" data-testid="pv-outlook-work">Work assessed: {outlook.work_assessed}</p>}
            {outlook.opening && <p className="mb-5 text-gray-800 leading-relaxed" data-testid="pv-outlook-opening">{outlook.opening}</p>}
            <KVBlocks obj={Object.fromEntries(visibleEntries(outlook).filter(([k]) => !['title', 'work_assessed', 'opening', 'reviewed_on'].includes(k)))} />
          </Section>
        )}

        {/* ── Subject Understanding gateway — FIRST report section (dark-navy card) ── */}
        {su?.available && (
          <section
            id="pv-subjects"
            className="rounded-3xl p-5 md:p-7 shadow-md scroll-mt-52 border border-teal-300/25"
            style={{ background: '#081827' }}
            data-testid="pv-section-subjects"
          >
            <p className="text-[10px] md:text-[11px] font-extrabold tracking-[0.3em] text-teal-300 uppercase mb-2.5">
              Layer 1 · Subjects
            </p>
            <h2
              className="text-xl md:text-2xl text-[#F7F3E6] mb-3"
              style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontStyle: 'italic' }}
              data-testid="pv-subjects-title"
            >
              {su.title}
            </h2>
            {su.intro && (
              <p className="text-slate-200/90 text-[14px] md:text-[15px] leading-relaxed mb-4" data-testid="pv-subjects-intro">
                {su.intro}
              </p>
            )}
            <ol className="flex flex-wrap gap-2 mb-5" data-testid="pv-subjects-modules">
              {(su.modules || []).map((m, i) => (
                <li key={m.subject_id} className="max-w-full"><button type="button" onClick={openSubjects} className="text-[12px] font-semibold text-slate-100 bg-white/10 hover:bg-white/20 border border-white/15 rounded-full px-3 py-1.5 break-words max-w-full text-left min-h-9 transition-colors" data-testid={`pv-subject-module-${m.subject_id}`}>
                  {i + 1}. {m.name}
                </button></li>
              ))}
            </ol>
            <button
              ref={subjectsBtnRef}
              type="button"
              onClick={openSubjects}
              className="inline-flex items-center gap-2 font-extrabold text-sm tracking-[0.18em] uppercase px-6 py-3 rounded-full hover:opacity-90 transition-opacity active:scale-95" style={{ background: '#FCD34D', color: '#081827' }}
              data-testid="pv-subjects-open-btn"
            >
              Open <ChevronRight size={15} />
            </button>
          </section>
        )}

        {/* snapshot — 5 score meters + best_for chips */}
        <Section id="pv-snapshot" icon={Layers} title={T('specialization-snapshot', 'Snapshot')} accent="teal" testId="pv-section-snapshot">
          {meters.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5 mb-6">
              {meters.map(([label, value, grad]) => <Meter key={label} label={label} value={value} grad={grad} />)}
            </div>
          )}
          {s.orientation && <div className="mb-6" data-testid="pv-snapshot-orientation"><KVBlocks obj={s.orientation} chipKeys={['foundations_to_explore']} /></div>}
          {s.ai_task_assessment && <div className="mb-6" data-testid="pv-snapshot-ai-task"><KVBlocks obj={s.ai_task_assessment} /></div>}
          <p className="font-extrabold text-gray-900 mb-2">Try this first</p>
          <BulletList items={s.best_for} />
          <PolicyNote text={rec.snapshot_note} />
        </Section>

        {/* what_this_specialization_means (+ boundary + how_this_differs) */}
        <Section id="pv-meaning" icon={BookOpen} title={T('what-this-specialization-really-means', 'What this specialization means')} accent="cyan" testId="pv-section-meaning">
          <p>{rec.what_this_specialization_means}</p>
          {rec.how_this_differs && (
            <p className="mt-4 bg-teal-50/70 border-2 border-teal-100 rounded-2xl px-4 py-3.5" data-testid="pv-how-differs">{rec.how_this_differs}</p>
          )}
          <PolicyNote text={rec.meaning_boundary} />
        </Section>

        {/* why_this_field_matters_now */}
        <Section id="pv-why-now" icon={TrendingUp} title={T('why-this-field-matters-now', 'Why this field matters now')} accent="emerald" testId="pv-section-why-now">
          {typeof rec.why_this_field_matters_now === 'object'
            ? <KVBlocks obj={rec.why_this_field_matters_now} />
            : <p>{rec.why_this_field_matters_now}</p>}
        </Section>

        {/* ai_agents_in_this_field */}
        <Section id="pv-ai-agents" icon={Bot} title={T('ai-agents-in-this-field', 'AI agents in this field')} accent="indigo" testId="pv-section-ai-agents">
          <KVBlocks obj={rec.ai_agents_in_this_field} />
        </Section>

        {/* human_ai_agent_workflow — 7-step timeline */}
        <Section id="pv-workflow" icon={Users} title={T('human-ai-agent-workflow', 'Human + AI-agent workflow')} accent="teal" testId="pv-section-workflow">
          <WorkflowTimeline steps={rec.human_ai_agent_workflow} />
          <PolicyNote text={rec.workflow_note} />
        </Section>

        {/* interactive_self_check_quiz */}
        <Section id="pv-quiz" icon={ClipboardCheck} title={T('interactive-self-check-quiz', 'Self-check: is this you?')} accent="amber" testId="pv-section-quiz">
          <SelfCheckQuiz questions={rec.interactive_self_check_quiz} resultLogic={rec.quiz_result_logic} />
          <PolicyNote text={rec.quiz_note} />
        </Section>

        {/* who_should_choose_this */}
        <Section id="pv-who" icon={User} title={T('who-should-choose-this', 'Who should choose this')} accent="rose" testId="pv-section-who">
          <BulletList items={rec.who_should_choose_this} />
        </Section>

        {/* what_students_will_learn */}
        <Section id="pv-learn" icon={GraduationCap} title={T('what-students-will-learn', 'What students will learn')} accent="purple" testId="pv-section-learn">
          <KVBlocks obj={rec.what_students_will_learn} />
        </Section>

        {/* tools_platforms_and_ai_services */}
        <Section id="pv-tools" icon={Wrench} title={T('tools-platforms-and-ai-services', 'Tools, platforms & AI services')} accent="indigo" testId="pv-section-tools">
          {rec.tool_guidance && <p className="mb-5" data-testid="pv-tool-guidance">{rec.tool_guidance}</p>}
          <KVBlocks obj={rec.tools_platforms_and_ai_services} chipKeys={Object.keys(rec.tools_platforms_and_ai_services || {})} />
          {rec.enterprise_context && (
            <div className="mt-6 bg-teal-50/70 border-2 border-teal-100 rounded-2xl px-4 py-4" data-testid="pv-enterprise-context">
              <p className="font-extrabold text-gray-900 mb-3">{rec.enterprise_context.heading || 'How companies can use AI in this work'}</p>
              <KVBlocks obj={Object.fromEntries(visibleEntries(rec.enterprise_context).filter(([k]) => k !== 'heading'))} />
            </div>
          )}
        </Section>

        {/* skills_required */}
        <Section id="pv-skills" icon={Sparkles} title={T('skills-required', 'Skills required')} accent="emerald" testId="pv-section-skills">
          {rec.skills_guidance && <p className="mb-5" data-testid="pv-skills-guidance">{rec.skills_guidance}</p>}
          <KVBlocks obj={rec.skills_required} />
        </Section>

        {/* ai_agent_integrated_projects */}
        <Section id="pv-projects" icon={Lightbulb} title={T('ai-agent-integrated-projects', 'AI-agent integrated projects')} accent="amber" testId="pv-section-projects">
          <KVBlocks obj={rec.ai_agent_integrated_projects} />
          {practice && (
            <div className="mt-6 bg-teal-50/70 border-2 border-teal-100 rounded-2xl px-4 py-4" data-testid="pv-ai-practice">
              <p className="text-[11px] uppercase tracking-widest font-extrabold text-teal-800 mb-1">Try it now · solved AI application</p>
              <p className="font-extrabold text-gray-900 mb-3" data-testid="pv-ai-practice-title">{practice.title}</p>
              <KVBlocks obj={Object.fromEntries(visibleEntries(practice).filter(([k]) => !['title', 'exercise_fixture', 'solution_steps', 'expected_result', 'changed_condition_expected_result'].includes(k)))} />
              <div className="flex flex-wrap gap-2 mt-4">
                {su?.available && practice.anchor_lesson_id && <button type="button" onClick={() => openSubjectTopic(practice.anchor_lesson_id)} className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-extrabold text-sm px-4 py-2.5 rounded-full transition-colors" data-testid="pv-ai-practice-open-lesson">Open the anchor lesson <ChevronRight size={15} /></button>}
                {su?.available && practice.case_id && <button type="button" onClick={() => openSubjectTopic(practice.case_id)} className="inline-flex items-center gap-2 bg-white border-2 border-teal-200 hover:border-teal-400 text-teal-800 font-extrabold text-sm px-4 py-2.5 rounded-full transition-colors" data-testid="pv-ai-practice-open-case">Open the specialization case <ChevronRight size={15} /></button>}
              </div>
              <p className="mt-3 text-sm text-gray-500 italic">Solved answers (original and changed condition) are shown inside the subject reader after your own attempt.</p>
            </div>
          )}
        </Section>

        {/* real_industry_scenario */}
        <Section id="pv-scenario" icon={Factory} title={T('illustrative-industry-scenario', 'Real industry scenario')} accent="rose" testId="pv-section-scenario">
          {rec.real_industry_scenario?.label && <p className="text-[11px] uppercase tracking-widest font-extrabold text-rose-700 mb-3">{rec.real_industry_scenario.label}</p>}
          <KVBlocks obj={Object.fromEntries(visibleEntries(rec.real_industry_scenario).filter(([k]) => k !== 'label'))} />
        </Section>

        {/* career_opportunities */}
        <Section id="pv-careers" icon={Briefcase} title={T('career-opportunities', 'Career opportunities')} accent="teal" testId="pv-section-careers">
          {rec.career_guidance && <p className="mb-5" data-testid="pv-career-guidance">{rec.career_guidance}</p>}
          <KVBlocks obj={rec.career_opportunities} chipKeys={Object.keys(rec.career_opportunities || {})} />
          {rec.how_jobs_and_hiring_will_change && (
            <div className="mt-6 bg-amber-50/70 border-2 border-amber-100 rounded-2xl px-4 py-4" data-testid="pv-hiring-change">
              <p className="font-extrabold text-gray-900 mb-3">How jobs and hiring may change</p>
              <KVBlocks obj={rec.how_jobs_and_hiring_will_change} />
            </div>
          )}
        </Section>

        {/* industries_hiring */}
        <Section id="pv-industries" icon={Building2} title={T('industries-to-check', 'Industries hiring')} accent="cyan" testId="pv-section-industries">
          {rec.industries_note && <p className="mb-3">{rec.industries_note}</p>}
          <ChipList items={rec.industries_hiring} tone="slate" />
        </Section>

        {/* higher_studies_and_certifications */}
        <Section id="pv-studies" icon={Award} title={T('higher-studies-and-certifications', 'Higher studies & certifications')} accent="purple" testId="pv-section-studies">
          <KVBlocks obj={rec.higher_studies_and_certifications} />
        </Section>

        {/* future_scope */}
        <Section id="pv-future" icon={Compass} title={T('future-scope', 'Future scope')} accent="emerald" testId="pv-section-future">
          <BulletList items={rec.future_scope} />
          <PolicyNote text={rec.future_scope_note} />
        </Section>

        {/* myths_vs_reality */}
        <Section id="pv-myths" icon={MessageSquareQuote} title={T('myths-vs-reality', 'Myths vs reality')} accent="rose" testId="pv-section-myths">
          <MythsReality items={rec.myths_vs_reality} />
          <PolicyNote text={rec.myths_note} />
        </Section>

        {/* readiness_roadmap — 4-stage stepper */}
        <Section id="pv-roadmap" icon={Map} title={T('readiness-roadmap', 'Readiness roadmap')} accent="teal" testId="pv-section-roadmap">
          {rec.study_strategy && <p className="mb-5" data-testid="pv-study-strategy">{rec.study_strategy}</p>}
          <RoadmapStepper stages={rec.readiness_roadmap} />
          <PolicyNote text={rec.roadmap_note} />
        </Section>

        {/* related_specializations — clickable within the same program */}
        <Section id="pv-related" icon={Link2} title={T('related-specializations', 'Related specializations')} accent="indigo" testId="pv-section-related">
          <div className="flex flex-wrap gap-2">
            {(rec.related_specializations || []).map((rel, i) => {
              const title = typeof rel === 'object' && rel ? rel.title : rel;
              const relId = typeof rel === 'object' && rel?.specialization_id
                ? parseInt(rel.specialization_id.split('-').pop(), 10) : null;
              return relId ? (
                <button
                  key={i}
                  onClick={() => navigate(`/specializations/preview/${programSlug(rel.program || program)}/${relId}`)}
                  className="text-sm px-3.5 py-1.5 rounded-full border-2 border-teal-300 bg-teal-50 text-teal-800 font-bold hover:bg-teal-100 transition-colors inline-flex items-center gap-1.5 active:scale-95"
                  data-testid={`pv-related-link-${relId}`}
                >
                  {title} <ChevronRight size={13} />
                </button>
              ) : (
                <span key={i} className="text-sm px-3.5 py-1.5 rounded-full border-2 border-gray-200 bg-gray-50 text-gray-600 font-medium" data-testid={`pv-related-plain-${i}`}>
                  {title}
                </span>
              );
            })}
          </div>
          <PolicyNote text={rec.related_notes} />
        </Section>

        {/* student_takeaway — highlighted callout */}
        <section id="pv-takeaway" className="rounded-3xl border-2 border-amber-300 bg-amber-50 p-5 md:p-7 scroll-mt-52 shadow-sm" data-testid="pv-section-takeaway">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-10 h-10 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center shadow-sm"><Sparkles size={18} /></span>
            <h2 className="text-lg md:text-xl font-extrabold text-amber-900">{T('student-takeaway', 'Student takeaway')}</h2>
          </div>
          <p className="text-base md:text-lg text-amber-950 leading-relaxed font-medium">{rec.student_takeaway}</p>
          {rec.takeaway_close && <p className="mt-3 text-amber-900 leading-relaxed">{rec.takeaway_close}</p>}
        </section>

        {/* PM Internship Opportunities — official portal link FIRST, then content */}
        {rec.pm_internship_opportunities && (
          <Section id="pv-pmis" icon={Briefcase} title={T('pm-internship-opportunities', 'PM Internship Opportunities')} accent="teal" testId="pv-section-pmis">
            {rec.pm_internship_opportunities.official_portal && (
              <a
                href={rec.pm_internship_opportunities.official_portal}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mb-5 bg-teal-50 border-2 border-teal-300 text-teal-800 font-extrabold text-sm md:text-base px-4 py-3 rounded-2xl hover:bg-teal-100 transition-colors break-all"
                data-testid="pv-pmis-portal-link"
              >
                <Link2 size={16} className="shrink-0" /> Official PM Internship Portal: {rec.pm_internship_opportunities.official_portal}
              </a>
            )}
            <KVBlocks obj={Object.fromEntries(Object.entries(rec.pm_internship_opportunities).filter(([k]) => k !== 'official_portal'))} />
          </Section>
        )}

        {/* what_changed — section 23 */}
        {rec.what_changed && (
          <Section id="pv-changes" icon={ClipboardCheck} title={rec.what_changed.title || T('what-changed', 'What changed in this preview')} accent="slate" testId="pv-section-changes">
            {rec.what_changed.updated_on && <p className="text-sm text-gray-500 mb-3" data-testid="pv-changes-date">Updated {rec.what_changed.updated_on}</p>}
            {rec.what_changed.returning_user_message && <p className="mb-4 bg-teal-50/70 border-2 border-teal-100 rounded-2xl px-4 py-3.5" data-testid="pv-changes-message">{rec.what_changed.returning_user_message}</p>}
            <div className="space-y-3">
              {(rec.what_changed.summary || []).map((row, i) => (
                <div key={i} className="bg-white border-2 border-gray-100 rounded-2xl px-4 py-3.5 shadow-sm" data-testid={`pv-change-${i}`}>
                  <p className="font-extrabold text-gray-900 mb-1">{row.area}</p>
                  <p className="text-gray-700">{row.updated}</p>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* closing_earning_options — section 24, final card with two existing destinations */}
        {earning && (
          <section id="pv-earning" className="rounded-3xl border-2 border-emerald-300 bg-emerald-50 p-5 md:p-7 scroll-mt-52 shadow-sm" data-testid="pv-section-earning">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-10 h-10 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 text-white flex items-center justify-center shadow-sm"><Briefcase size={18} /></span>
              <h2 className="text-lg md:text-xl font-extrabold text-emerald-950">{earning.section_title || T('practical-earning-options', 'Build a practical earning skill alongside your studies')}</h2>
            </div>
            <p className="text-base text-emerald-950 leading-relaxed" data-testid="pv-earning-intro">{earning.intro}</p>
            {earning.for_your_studies && <p className="mt-3 text-emerald-900 leading-relaxed">{earning.for_your_studies}</p>}
            <div className="flex flex-wrap gap-3 mt-5">
              {(earning.actions || []).map((a) => (
                <a key={a.action_id} href={a.url.replace(/^https?:\/\/(www\.)?learnixta\.com/, '')} className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold px-5 py-3 rounded-full transition-colors" title={a.description} data-testid={`pv-earning-action-${a.action_id}`}>
                  {a.label} <ChevronRight size={15} />
                </a>
              ))}
            </div>
            <details className="mt-5 text-emerald-950" data-testid="pv-earning-details">
              <summary className="cursor-pointer font-bold min-h-11 flex items-center">Examples, checks and source notes</summary>
              <div className="mt-3 text-gray-700">
                <KVBlocks obj={Object.fromEntries(visibleEntries(earning).filter(([k]) => !['section_title', 'intro', 'for_your_studies', 'actions', 'reviewed_on'].includes(k)))} />
                {earning.reviewed_on && <p className="mt-4 text-sm text-gray-500">Reviewed {earning.reviewed_on}</p>}
              </div>
            </details>
          </section>
        )}

        {/* Build Badge for THIS specialization — strict same-commit-and-lock.
            Routes to the gated /specializations/preview/:program/:id/badge page
            which serves only this spec's mapped brief. No catalogue browse. */}
        <div
          className="my-10 rounded-3xl bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 text-white p-6 md:p-8 shadow-xl"
          data-testid="spec-build-badge-cta"
        >
          <div className="flex items-start gap-4 flex-wrap">
            <span className="inline-flex w-12 h-12 rounded-2xl bg-white/15 backdrop-blur items-center justify-center shrink-0">
              <Award size={22} />
            </span>
            <div className="flex-1 min-w-[220px]">
              <span className="text-[10px] font-extrabold tracking-widest uppercase bg-white/15 px-2 py-0.5 rounded-full">
                Build Badge · Free · Bronze tier · For this specialization
              </span>
              <h3 className="text-xl md:text-2xl font-black mt-2 mb-1">
                Get the Build Badge for {rec.title}
              </h3>
              <p className="text-white/90 text-[14px] md:text-[15px] leading-relaxed">
                Don&rsquo;t just read about this — <strong>build a small project that proves you can do it.</strong> AI-reviewed in seconds. Sharable on LinkedIn forever.
              </p>
            </div>
            <Link
              to={`/specializations/preview/${slug}/${id}/badge`}
              data-testid="spec-build-badge-link"
              className="inline-flex items-center gap-2 bg-white text-amber-700 font-extrabold px-5 py-3 rounded-full hover:bg-amber-50 transition-colors shrink-0"
            >
              See this badge brief <ChevronRight size={15} />
            </Link>
          </div>
        </div>

        {/* Ask Lexi about this specialization */}
        <LexiTrigger
          variant="card"
          prefill={`I'm reading the ${rec.title} (${rec.program}) preview. Can you help me decide if it fits me and what I should do this month to test my interest?`}
          label={`Ask Lexi about ${rec.title}`}
          description="Get India-specific guidance on this specialization"
          testId="pv-lexi-trigger"
        />

        <LiveOneDayButton pathKey="specializations" itemId={`${slug}_${id}`} roleName={rec.title} />
      </main>

      {subjectsOpen && su?.available && (
        <SubjectUnderstanding
          itemId={specId}
          apiBase={`${process.env.REACT_APP_BACKEND_URL}/api/specs/subject-understanding`}
          contextLabel="Inner Specializations · Your subject route"
          onClose={closeSubjects}
        />
      )}

      <Footer />
    </div>
    </PreviewUnlockOverlay>
  );
}
